import type { Channel } from '../types';

export interface WelcomeDraft {
  card: string;
  website: string;
  websiteRead?: boolean;
  noWebsite?: boolean;
  name: string;
  about: string;
  language?: 'pl' | 'en' | 'both';
  sampleLanguage?: 'pl' | 'en';
  logo?: { name: string; src: string; fromSite?: boolean };
  logoSkipped?: boolean;
  colours: { hex: string; keep: boolean }[];
  coloursByAmy?: boolean;
  photo?: { name: string; src: string; from: 'phone' | 'drive' | 'post' };
  photoSkipped?: boolean;
  likes: string[];
  likeText: string;
  tone?: 'warm' | 'clean' | 'playful' | 'own' | 'later';
  ownTone: string;
  avoid: string;
  profiles: { network: Channel; name: string }[];
  skipProfiles?: boolean;
  topicKind?: 'product' | 'event' | 'intro' | 'own';
  topic: string;
  eventDate?: string;
  round: number;
  feedback: string[];
  look?: number;
  caption?: number;
  summaryLook?: string;
  summaryCaption?: string;
  done?: boolean;
  postId?: string;
}

export const emptyDraft = (): WelcomeDraft => ({
  card: 'website',
  website: '',
  name: '',
  about: '',
  colours: [],
  likes: [],
  likeText: '',
  ownTone: '',
  avoid: '',
  profiles: [],
  topic: '',
  round: 0,
  feedback: [],
});

export const CARDS: { id: string; stage: 0 | 1 | 2 | 3; required?: boolean }[] = [
  { id: 'website', stage: 0 },
  { id: 'name', stage: 0, required: true },
  { id: 'about', stage: 0, required: true },
  { id: 'language', stage: 0 },
  { id: 'logo', stage: 1 },
  { id: 'colours', stage: 1 },
  { id: 'photo', stage: 1 },
  { id: 'likes', stage: 1 },
  { id: 'tone', stage: 1 },
  { id: 'profiles', stage: 2 },
  { id: 'topic', stage: 3, required: true },
  { id: 'samples', stage: 3, required: true },
  { id: 'summary', stage: 3 },
];
export const STAGES = ['Your business', 'Your style', 'Your channels', 'Sample posts'];

const svgDataUrl = (svgBody: string) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 80" width="240" height="80">${svgBody}</svg>`)}`;
export const DEMO_LOGO = svgDataUrl(
  '<rect width="240" height="80" rx="12" fill="#F4EDE4"/><path d="M40 16c14 10 18 26 12 38-4 8-10 11-12 14-2-3-8-6-12-14-6-12-2-28 12-38z" fill="#4F7A5A"/><text x="72" y="50" font-family="Georgia, serif" font-size="30" fill="#2F2A26">Zielna</text>'
);
export const DEMO_SITE = {
  url: 'kawiarnia-zielna.pl',
  name: 'Kawiarnia Zielna',
  about: {
    pl: 'Mała kawiarnia na Żoliborzu w Warszawie. Kawa z lokalnej palarni, domowe ciasta i śniadania do południa.',
    en: 'A small café in Żoliborz, Warsaw. Coffee from a local roaster, homemade cakes and breakfast until noon.',
  },
  colours: ['#4F7A5A', '#F4EDE4', '#2F2A26', '#C9824A'],
};

export function readWebsite(url: string): {
  name: string;
  about?: { pl: string; en: string };
  logo?: string;
  colours?: string[];
} {
  const host = url
    .trim()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .split('/')[0];
  if (host === DEMO_SITE.url)
    return {
      name: DEMO_SITE.name,
      about: DEMO_SITE.about,
      logo: DEMO_LOGO,
      colours: DEMO_SITE.colours,
    };
  const domainNameWithoutSuffix = host.split('.')[0] ?? '';
  const guessedBusinessName = domainNameWithoutSuffix
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word[0]?.toUpperCase() + word.slice(1))
    .join(' ');
  return { name: guessedBusinessName };
}

const SAMPLE_CANVAS_SIZE_IN_PIXELS = 32;
const BYTES_PER_PIXEL = 4;
const MINIMUM_VISIBLE_ALPHA = 128;
const BITS_DROPPED_PER_CHANNEL_WHEN_BUCKETING = 5;
const DOMINANT_COLOUR_COUNT = 4;

interface ColourBucket {
  pixelCount: number;
  redSum: number;
  greenSum: number;
  blueSum: number;
}

const toHexByte = (channelValue: number) => Math.round(channelValue).toString(16).padStart(2, '0');

const averageColourHex = (bucket: ColourBucket) =>
  `#${toHexByte(bucket.redSum / bucket.pixelCount)}${toHexByte(bucket.greenSum / bucket.pixelCount)}${toHexByte(bucket.blueSum / bucket.pixelCount)}`.toUpperCase();

export async function coloursFromImage(src: string): Promise<string[]> {
  const image = new Image();
  image.crossOrigin = 'anonymous';
  image.src = src;
  await image.decode().catch(() => undefined);
  const canvas = document.createElement('canvas');
  canvas.width = SAMPLE_CANVAS_SIZE_IN_PIXELS;
  canvas.height = SAMPLE_CANVAS_SIZE_IN_PIXELS;
  const drawingContext = canvas.getContext('2d');
  if (!drawingContext || !image.width) return [];
  try {
    drawingContext.drawImage(
      image,
      0,
      0,
      SAMPLE_CANVAS_SIZE_IN_PIXELS,
      SAMPLE_CANVAS_SIZE_IN_PIXELS
    );
  } catch {
    return [];
  }
  let pixelBytes: Uint8ClampedArray;
  try {
    pixelBytes = drawingContext.getImageData(
      0,
      0,
      SAMPLE_CANVAS_SIZE_IN_PIXELS,
      SAMPLE_CANVAS_SIZE_IN_PIXELS
    ).data;
  } catch {
    return [];
  }
  const bucketsByCoarseColour = new Map<string, ColourBucket>();
  for (let byteIndex = 0; byteIndex < pixelBytes.length; byteIndex += BYTES_PER_PIXEL) {
    const red = pixelBytes[byteIndex];
    const green = pixelBytes[byteIndex + 1];
    const blue = pixelBytes[byteIndex + 2];
    const alpha = pixelBytes[byteIndex + 3];
    if (alpha < MINIMUM_VISIBLE_ALPHA) continue;
    const coarseColourKey = [red, green, blue]
      .map((channelValue) => channelValue >> BITS_DROPPED_PER_CHANNEL_WHEN_BUCKETING)
      .join('-');
    const bucket = bucketsByCoarseColour.get(coarseColourKey) ?? {
      pixelCount: 0,
      redSum: 0,
      greenSum: 0,
      blueSum: 0,
    };
    bucket.pixelCount++;
    bucket.redSum += red;
    bucket.greenSum += green;
    bucket.blueSum += blue;
    bucketsByCoarseColour.set(coarseColourKey, bucket);
  }
  return [...bucketsByCoarseColour.values()]
    .sort((first, second) => second.pixelCount - first.pixelCount)
    .slice(0, DOMINANT_COLOUR_COUNT)
    .map(averageColourHex);
}

export const LIKE_CHIPS = [
  'Warm light',
  'Real people',
  'Close-ups',
  'Bright colours',
  'Calm and minimal',
  'Natural, not posed',
];

export const LOOKS = [
  {
    id: 0,
    name: 'Warm and personal',
    filter: 'sepia(.25) saturate(1.15) brightness(1.03)',
    summary: {
      pl: 'ciepłe, naturalne zdjęcia z prawdziwego miejsca, kremowe tło, logo dyskretnie w rogu',
      en: 'warm, natural photos from the real place, cream background, logo small in the corner',
    },
  },
  {
    id: 1,
    name: 'Clean and simple',
    filter: 'saturate(.85) contrast(1.05)',
    summary: {
      pl: 'jasne, uporządkowane kadry z dużą ilością wolnej przestrzeni i jednym kolorem marki',
      en: 'light, tidy shots with plenty of space and one brand colour',
    },
  },
  {
    id: 2,
    name: 'Bright and playful',
    filter: 'saturate(1.45) contrast(1.1)',
    summary: {
      pl: 'mocne kolory marki, wyraźny pasek z hasłem, energiczne kadry',
      en: 'bold brand colours, a clear headline band, energetic shots',
    },
  },
];

const topicText = (draft: WelcomeDraft, language: 'pl' | 'en') => {
  const ownTopic = draft.topic.trim();
  if (draft.topicKind === 'intro' || !ownTopic)
    return language === 'pl' ? `Przedstawiamy się: ${draft.name}` : `Meet ${draft.name}`;
  return ownTopic;
};

export function sampleCaption(draft: WelcomeDraft, style: number, language: 'pl' | 'en'): string {
  const topic = topicText(draft, language);
  const isDatedEvent = draft.topicKind === 'event' && draft.eventDate;
  const when = isDatedEvent ? ` – ${draft.eventDate}` : '';
  const name = draft.name || (language === 'pl' ? 'nas' : 'us');
  const polishCaptions = [
    `${topic}${when}. Wpadnij do nas – zrobimy Ci kawę i chętnie opowiemy o szczegółach. ${name} zaprasza! ☕`,
    `${topic}${when}. ${name} – zapraszamy.`,
    `🎉 ${topic}${when}! Kto pierwszy, ten lepszy – oznacz osobę, z którą do nas wpadniesz! ${name} ✨`,
  ];
  const englishCaptions = [
    `${topic}${when}. Come and see us at ${name} – we will make you a coffee and tell you all about it. See you soon! ☕`,
    `${topic}${when}. ${name}. You are welcome.`,
    `🎉 ${topic}${when}! First come, first served – tag the friend you are bringing to ${name}! ✨`,
  ];
  const polishCaptionsAfterNoneOfThese = [
    `U nas: ${topic.toLowerCase()}${when}. Przyjdź, usiądź przy oknie i spróbuj na miejscu. ${name}`,
    `${topic}${when}. Sprawdź na miejscu – ${name}.`,
    `Uwaga, uwaga: ${topic.toLowerCase()}${when}! Widzimy się? 🙌 ${name}`,
  ];
  const englishCaptionsAfterNoneOfThese = [
    `At ${name}: ${topic.toLowerCase()}${when}. Come by, take a seat by the window and try it yourself.`,
    `${topic}${when} – at ${name}. Come and see.`,
    `Heads up: ${topic.toLowerCase()}${when}! See you at ${name}? 🙌`,
  ];
  const showingRewrittenSet = draft.round % 2 === 1;
  const firstSet = language === 'pl' ? polishCaptions : englishCaptions;
  const rewrittenSet =
    language === 'pl' ? polishCaptionsAfterNoneOfThese : englishCaptionsAfterNoneOfThese;
  const captionSet = showingRewrittenSet ? rewrittenSet : firstSet;
  return captionSet[style];
}
export const CAPTION_NAMES = ['Warm and personal', 'Short and clear', 'Lively and playful'];
export const captionSummary = (
  style: number,
  language: 'pl' | 'en',
  sampleLanguage: 'pl' | 'en'
) => {
  const polishSampleLanguageName = language === 'pl' ? 'po polsku' : 'Polish';
  const englishSampleLanguageName = language === 'pl' ? 'po angielsku' : 'English';
  const sampleLanguageName =
    sampleLanguage === 'pl' ? polishSampleLanguageName : englishSampleLanguageName;
  return language === 'pl'
    ? [
        `ciepłe, osobiste podpisy ${sampleLanguageName}, z zaproszeniem i jednym emoji`,
        `krótkie, rzeczowe podpisy ${sampleLanguageName}, bez emoji`,
        `żywe podpisy ${sampleLanguageName} z emoji i pytaniem do odbiorców`,
      ][style]
    : [
        `warm, personal ${sampleLanguageName} captions with an invitation and one emoji`,
        `short, factual ${sampleLanguageName} captions without emoji`,
        `lively ${sampleLanguageName} captions with emoji and a question for followers`,
      ][style];
};
export const toneExamples = (name: string, language: 'pl' | 'en') =>
  language === 'pl'
    ? [
        `Dzień dobry! Czeka na Ciebie świeża kawa i ciasto prosto z pieca. ${name || ''}`.trim(),
        `Świeża kawa i ciasto z pieca. ${name ? `${name} – zapraszamy.` : 'Zapraszamy.'}`,
        `Kawa gotowa, ciasto ciepłe – kto do nas dziś wpada? 😄`,
      ]
    : [
        `Good morning! Fresh coffee and cake straight from the oven are waiting for you at ${name || 'our place'}.`,
        `Fresh coffee, cake from the oven. ${name || ''} – welcome.`.trim(),
        `Coffee's ready, cake's warm – who's dropping by ${name || 'us'} today? 😄`,
      ];
